'use client';
import {
  CaretDoubleLeft,
  CaretDoubleRight,
  CaretLeft,
  CaretRight,
} from 'phosphor-icons';
import { useMemo } from 'react';
import { PaginationProps, PaginationState } from './Pagination.types';

const commonButtonClasses =
  'size-10 mx-1 rounded-lg cursor-pointer border text-content-subtle border-line-medium text-base hover:bg-surface-muted flex items-center justify-center transition-colors';

const arrowButtonStyles = `${commonButtonClasses} disabled:bg-line-medium disabled:cursor-not-allowed disabled:text-line-strong`;

const pageButtonStyles = `${commonButtonClasses} font-bold disabled:cursor-not-allowed`;

// The current page renders as a disabled button; these keep its highlight
// distinct from pages that are disabled because the whole control is.
const currentPageStyles =
  'disabled:bg-accent disabled:border-accent disabled:text-accent-content';
const inertPageStyles = 'disabled:bg-line-medium disabled:text-line-strong';

export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  showFirstLast = false,
  maxVisiblePages = 5,
  className = '',
  disabled = false,
  microcopy,
}: PaginationProps) => {
  const {
    paginationAriaLabel,
    previousAriaLabel,
    nextAriaLabel,
    pageAriaLabel,
    firstAriaLabel,
    lastAriaLabel,
  } = microcopy;

  const paginationState: PaginationState = useMemo(() => {
    const hasPreviousPage = currentPage > 1;
    const hasNextPage = currentPage < totalPages;

    // Calculate visible pages
    const visiblePages: number[] = [];
    const halfVisible = Math.floor(maxVisiblePages / 2);

    let startPage = Math.max(1, currentPage - halfVisible);
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust start if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      visiblePages.push(i);
    }

    return {
      currentPage,
      totalPages,
      hasPreviousPage,
      hasNextPage,
      visiblePages,
    };
  }, [currentPage, totalPages, maxVisiblePages]);

  const handlePageChange = (page: number) => {
    onPageChange(page);
  };

  const handlePreviousPage = () => {
    const newPage = Math.max(1, currentPage - 1);
    handlePageChange(newPage);
  };

  const handleNextPage = () => {
    const newPage = Math.min(totalPages, currentPage + 1);
    handlePageChange(newPage);
  };

  const handleFirstPage = () => {
    handlePageChange(1);
  };

  const handleLastPage = () => {
    handlePageChange(totalPages);
  };

  const isCurrentPage = (page: number) => page === currentPage;

  if (totalPages <= 1) {
    return null;
  }

  return (
    <nav
      data-test="pagination"
      aria-label={paginationAriaLabel}
      className={className}
    >
      <ul className="flex">
        {showFirstLast && paginationState.hasPreviousPage && (
          <li>
            <button
              aria-label={firstAriaLabel}
              className={arrowButtonStyles}
              disabled={disabled}
              onClick={handleFirstPage}
            >
              <CaretDoubleLeft size={20} role="presentation" />
            </button>
          </li>
        )}

        <li>
          <button
            aria-label={previousAriaLabel}
            className={arrowButtonStyles}
            disabled={disabled || !paginationState.hasPreviousPage}
            aria-disabled={disabled || !paginationState.hasPreviousPage}
            onClick={handlePreviousPage}
          >
            <CaretLeft size={20} role="presentation" />
          </button>
        </li>

        {paginationState.visiblePages.map((page) => (
          <li key={page}>
            <button
              aria-label={`${pageAriaLabel} ${page}`}
              className={`${pageButtonStyles} ${
                isCurrentPage(page) ? currentPageStyles : inertPageStyles
              }`}
              disabled={disabled || isCurrentPage(page)}
              aria-current={isCurrentPage(page) ? 'page' : undefined}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          </li>
        ))}

        <li>
          <button
            aria-label={nextAriaLabel}
            className={arrowButtonStyles}
            disabled={disabled || !paginationState.hasNextPage}
            aria-disabled={disabled || !paginationState.hasNextPage}
            onClick={handleNextPage}
          >
            <CaretRight size={20} role="presentation" />
          </button>
        </li>

        {showFirstLast && paginationState.hasNextPage && (
          <li>
            <button
              aria-label={lastAriaLabel}
              className={arrowButtonStyles}
              disabled={disabled}
              onClick={handleLastPage}
            >
              <CaretDoubleRight size={20} role="presentation" />
            </button>
          </li>
        )}
      </ul>
    </nav>
  );
};
